import { useLDFlagValue } from '@/data/LD/selectors/getLDFlag';

interface RipplingXsConfig {
  ctaBaseUrl?: string;
  smallBusinessQueueName?: string;
  midMarketQueueName?: string;
  enterpriseQueueName?: string;
  leadRouting?: string;
}

interface RipplingLinkOptions {
  campaign: string;
  source?: string;
  medium?: string;
}

interface RipplingLinkResult {
  url: string;
  isExternal: boolean;
}

/**
 * Utility hook to generate Rippling CTA links with proper UTM parameters
 * and determine if the link is external or internal
 */
export function useRipplingLink(options: RipplingLinkOptions): RipplingLinkResult {
  const ripplingConfig = useLDFlagValue<RipplingXsConfig>('rippling-xs-configuration');

  const baseUrl = ripplingConfig?.ctaBaseUrl || '/splash/rippling';
  const isExternal = baseUrl.startsWith('http');

  const utmParams = new URLSearchParams({
    utm_source: options.source || 'wiw-app',
    utm_medium: options.medium || 'link',
    utm_campaign: options.campaign,
  }).toString();

  let finalUrl: string;

  if (isExternal) {
    // For external URLs, append query params properly
    const separator = baseUrl.includes('?') ? '&' : '?';
    finalUrl = `${baseUrl}${separator}${utmParams}`;
  } else {
    // For internal routes, append query params
    finalUrl = `${baseUrl}?${utmParams}`;
  }

  return {
    url: finalUrl,
    isExternal,
  };
}

/**
 * Default configuration values for Rippling cross-sell features.
 * These values are used as fallbacks when LaunchDarkly configuration is unavailable.
 */
const DEFAULT_RIPPLING_CONFIG: RipplingXsConfig = {
  ctaBaseUrl: '/splash/rippling',
  smallBusinessQueueName: 'WiW SB',
  midMarketQueueName: 'WiW MM',
  enterpriseQueueName: 'WiW ENT',
  leadRouting: '701Ud00000SyLMVIA3',
};

/**
 * Hook to get the full Rippling cross-sell configuration from LaunchDarkly.
 * Returns default values if the LaunchDarkly flag is unavailable or fails to load.
 */
export function useRipplingXsConfig(): RipplingXsConfig {
  const ldConfig = useLDFlagValue<RipplingXsConfig>('rippling-xs-configuration');

  // Merge LaunchDarkly config with defaults, giving precedence to LD values
  return {
    ...DEFAULT_RIPPLING_CONFIG,
    ...ldConfig,
  };
}
